import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { CLOUD_API_URL } from '../config/serverConfig';
import { isSessionDead } from '../lib/sessionDead';
import { formatBillingError, openBillingPortal } from '../services/subscriptionService';
import { logBillingCheckoutFailure } from '../utils/billingDiagnostics';
import { navigateToSubscribe, shouldPreferBrowserCheckout, startHostedCheckout } from '../utils/billingHandoff';
import { isIosCommerceBlocked } from '../utils/iosCommerceGate';
import { withPaidCheckoutConsent } from '../utils/paidCheckoutConsent';
import { shouldBlockAccess, shouldIgnoreAlreadySubscribed, shouldShowAlreadySubscribed } from '../utils/billingEntitlement';
import { useQobuzStore } from '../lib/useQobuz';

const SubscriptionContext = createContext(null);

export function useSubscription() {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) throw new Error('useSubscription must be used within SubscriptionProvider');
  return ctx;
}

export default function SubscriptionProvider({ children }) {
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [previewMode, setPreviewMode] = useState(null);
  const [stripeSlideout, setStripeSlideout] = useState({ open: false, clientSecret: null });
  const [showSubscribedWelcome, setShowSubscribedWelcome] = useState(false);
  const [previewBannerDismissed, setPreviewBannerDismissed] = useState(false);
  const [previewFlash, setPreviewFlash] = useState(false);
  const [trialExpired, setTrialExpired] = useState(false);
  /** null | verifying | success | processing | failed — checkout return URL verification */
  const [checkoutReturnState, setCheckoutReturnState] = useState(null);
  const didAutoFetch = useRef(false);
  const checkoutVerifyStarted = useRef(false);

  const navigateToBillingPortal = useCallback(async () => {
    if (isIosCommerceBlocked()) return false;
    const result = await openBillingPortal();
    if (result.status === 'portal') {
      window.location.href = result.url;
      return true;
    }
    if (result.status === 'checkout') {
      navigateToSubscribe();
      return false;
    }
    const { showImperativeToast } = await import('./UIProvider');
    showImperativeToast(result.message || 'Unable to open billing portal.', 6000);
    return false;
  }, []);

  const fetchSubscriptionStatus = useCallback(async () => {
    if (isSessionDead()) return null;
    const token = (() => { try { const s = localStorage.getItem('maestro_auth'); return s ? JSON.parse(s).accessToken : null; } catch { return null; } })();
    console.log('[SubStatus] token present:', !!token);
    if (!token) return null;
    try {
      const res = await fetch(`${CLOUD_API_URL}/api/stripe/subscription-status`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'omit',
      });
      console.log('[SubStatus] response status:', res.status);
      if (res.ok) {
        const data = await res.json();
        console.log('[SubStatus] data:', JSON.stringify(data));
        console.log('[SubStatus] shouldBlock:', shouldBlockAccess(data));
        setSubscriptionData(data);
        setTrialExpired(shouldBlockAccess(data));
        return data;
      }
    } catch (e) { console.error('[SubStatus] fetch error:', e); }
    return null;
  }, []);

  const openStripeCheckout = useCallback(async (cadence = 'monthly') => {
    if (isIosCommerceBlocked()) return false;
    const token = (() => { try { const s = localStorage.getItem('maestro_auth'); return s ? JSON.parse(s).accessToken : null; } catch { return null; } })();
    if (!token) {
      const { showImperativeToast } = await import('./UIProvider');
      showImperativeToast('Please sign in to manage your subscription.', 5000);
      return false;
    }
    const ctx = subscriptionData;
    if (shouldPreferBrowserCheckout()) {
      try {
        const hosted = await startHostedCheckout(cadence);
        if (hosted.ok && hosted.alreadySubscribed) {
          const freshCtx = await fetchSubscriptionStatus() || ctx;
          if (shouldIgnoreAlreadySubscribed(freshCtx)) {
            logBillingCheckoutFailure('SubscriptionProvider', {
              status: 0,
              error: hosted.message || 'already_subscribed while checkout still required',
              tier: freshCtx?.subscription_tier,
              had_trial: freshCtx?.had_trial,
              stripe_customer_id: !!freshCtx?.stripe_customer_id,
              already_subscribed: true,
            });
            const { showImperativeToast } = await import('./UIProvider');
            showImperativeToast(hosted.error || 'Unable to start checkout. Please try again.', 6000);
            return false;
          }
          if (!shouldShowAlreadySubscribed(freshCtx)) {
            const { showImperativeToast } = await import('./UIProvider');
            showImperativeToast(hosted.error || 'Unable to start checkout. Please try again.', 6000);
            return false;
          }
          const opened = await navigateToBillingPortal();
          if (!opened) {
            const { showImperativeToast } = await import('./UIProvider');
            showImperativeToast(hosted.message || "You're already subscribed — opening billing…", 5000);
          }
          return true;
        }
        if (hosted.ok) {
          const { showImperativeToast } = await import('./UIProvider');
          showImperativeToast(
            hosted.opened
              ? 'Finish checkout in your browser, then return to Maestro.'
              : 'Open checkout in your browser to subscribe.',
            7000,
          );
          return true;
        }
        logBillingCheckoutFailure('SubscriptionProvider', {
          status: 0,
          error: hosted.error || 'hosted checkout failed',
          tier: ctx?.subscription_tier,
          had_trial: ctx?.had_trial,
          stripe_customer_id: !!ctx?.stripe_customer_id,
        });
        const { showImperativeToast } = await import('./UIProvider');
        showImperativeToast(hosted.error || 'Unable to start checkout. Please try again.', 6000);
        return false;
      } catch (e) {
        logBillingCheckoutFailure('SubscriptionProvider', {
          status: 0,
          error: e?.message || 'hosted checkout error',
          tier: ctx?.subscription_tier,
          had_trial: ctx?.had_trial,
          stripe_customer_id: !!ctx?.stripe_customer_id,
        });
        const { showImperativeToast } = await import('./UIProvider');
        showImperativeToast('Unable to reach the billing server. Please check your connection and try again.', 6000);
        return false;
      }
    }
    try {
      const r = await fetch(`${CLOUD_API_URL}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'omit',
        body: JSON.stringify(withPaidCheckoutConsent({ ui_mode: 'embedded', cadence, trial_pitch: ctx?.subscription_tier === 'trial' }, true)),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.already_subscribed && d.ok) {
        const freshCtx = await fetchSubscriptionStatus() || ctx;
        if (shouldIgnoreAlreadySubscribed(freshCtx)) {
          logBillingCheckoutFailure('SubscriptionProvider', {
            status: r.status,
            error: d.message || 'already_subscribed while checkout still required',
            tier: freshCtx?.subscription_tier,
            had_trial: freshCtx?.had_trial,
            stripe_customer_id: !!freshCtx?.stripe_customer_id,
            already_subscribed: true,
          });
          const { showImperativeToast } = await import('./UIProvider');
          showImperativeToast(formatBillingError(d, 'Unable to start checkout. Please try again.'), 6000);
          return false;
        }
        if (!shouldShowAlreadySubscribed(freshCtx)) {
          const { showImperativeToast } = await import('./UIProvider');
          showImperativeToast(formatBillingError(d, 'Unable to start checkout. Please try again.'), 6000);
          return false;
        }
        const opened = await navigateToBillingPortal();
        if (!opened) {
          const { showImperativeToast } = await import('./UIProvider');
          showImperativeToast(d.message || "You're already subscribed — opening billing…", 5000);
        }
        return true;
      }
      if (d.clientSecret) {
        setStripeSlideout({ open: true, clientSecret: d.clientSecret });
        return true;
      }
      logBillingCheckoutFailure('SubscriptionProvider', {
        status: r.status,
        error: d.error || d.message || 'no clientSecret',
        tier: ctx?.subscription_tier,
        had_trial: ctx?.had_trial,
        stripe_customer_id: !!ctx?.stripe_customer_id,
        already_subscribed: !!d.already_subscribed,
        stripe_code: d.stripe_code,
        request_id: d.request_id,
      });
      const { showImperativeToast } = await import('./UIProvider');
      const msg = formatBillingError(
        d,
        r.status === 409
          ? 'You already have an active subscription. Check Settings → Subscription to manage it.'
          : 'Unable to start checkout. Please try again or contact support.',
      );
      showImperativeToast(msg, 6000);
      return false;
    } catch (e) {
      logBillingCheckoutFailure('SubscriptionProvider', {
        status: 0,
        error: e?.message || 'network error',
        tier: ctx?.subscription_tier,
        had_trial: ctx?.had_trial,
        stripe_customer_id: !!ctx?.stripe_customer_id,
      });
      console.error('[Stripe]', e);
      const { showImperativeToast } = await import('./UIProvider');
      showImperativeToast('Unable to reach the billing server. Please check your connection and try again.', 6000);
      return false;
    }
  }, [subscriptionData, fetchSubscriptionStatus, navigateToBillingPortal]);

  useEffect(() => {
    const token = (() => { try { const s = localStorage.getItem('maestro_auth'); return s ? JSON.parse(s).accessToken : null; } catch { return null; } })();
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${CLOUD_API_URL}/api/user/streaming`, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'omit',
        });
        if (!cancelled && res.ok) {
          const data = await res.json();
          setPreviewMode(data?.streaming_mode === 'full' ? 'full' : 'preview');
        }
      } catch {}
      if (!cancelled && useQobuzStore.getState().connected) setPreviewMode('full');
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const unsub = useQobuzStore.subscribe((state) => {
      if (state.connected) setPreviewMode('full');
    });
    return unsub;
  }, []);

  useEffect(() => {
    const handler = () => {
      setPreviewFlash(true);
      setTimeout(() => setPreviewFlash(false), 3000);
    };
    window.addEventListener('maestro:preview-ended', handler);
    return () => window.removeEventListener('maestro:preview-ended', handler);
  }, []);

  useEffect(() => {
    if (checkoutVerifyStarted.current) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('subscribed') !== 'true') return;
    checkoutVerifyStarted.current = true;

    const sessionId = params.get('session_id');
    const stripReturnParams = () => {
      const url = new URL(window.location.href);
      url.searchParams.delete('subscribed');
      url.searchParams.delete('session_id');
      window.history.replaceState({}, '', url.pathname + url.search + url.hash);
    };
    stripReturnParams();

    if (!sessionId) {
      setCheckoutReturnState('failed');
      return;
    }

    const token = (() => { try { const s = localStorage.getItem('maestro_auth'); return s ? JSON.parse(s).accessToken : null; } catch { return null; } })();
    if (!token) {
      setCheckoutReturnState('failed');
      return;
    }

    setCheckoutReturnState('verifying');
    let cancelled = false;

    (async () => {
      try {
        const res = await fetch(
          `${CLOUD_API_URL}/api/stripe/verify-checkout-session?session_id=${encodeURIComponent(sessionId)}`,
          { headers: { Authorization: `Bearer ${token}` }, credentials: 'omit' },
        );
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;

        if (!res.ok) {
          setCheckoutReturnState('failed');
          return;
        }

        if (data.entitled) {
          setCheckoutReturnState('success');
          setShowSubscribedWelcome(true);
          setTrialExpired(false);
          await fetchSubscriptionStatus();
          return;
        }

        const paymentStatus = String(data.payment_status || '');
        const subStatus = String(data.subscription_status || '');
        if (paymentStatus === 'unpaid' || subStatus === 'incomplete' || subStatus === 'processing') {
          setCheckoutReturnState('processing');
          await fetchSubscriptionStatus();
          return;
        }

        setCheckoutReturnState('failed');
      } catch (e) {
        console.error('[SubStatus] verify-checkout-session error:', e);
        if (!cancelled) setCheckoutReturnState('failed');
      }
    })();

    return () => { cancelled = true; };
  }, [fetchSubscriptionStatus]);

  useEffect(() => {
    const tier = subscriptionData?.subscription_tier;
    if (checkoutReturnState === 'processing' && tier && ['active', 'trial', 'grace'].includes(tier)) {
      setCheckoutReturnState('success');
      setShowSubscribedWelcome(true);
      setTrialExpired(false);
    }
  }, [subscriptionData?.subscription_tier, checkoutReturnState]);

  // Auto-fetch subscription status on mount when authenticated
  useEffect(() => {
    if (didAutoFetch.current) return;
    const token = (() => { try { const s = localStorage.getItem('maestro_auth'); return s ? JSON.parse(s).accessToken : null; } catch { return null; } })();
    if (!token) return;
    didAutoFetch.current = true;
    fetchSubscriptionStatus();
  }, [fetchSubscriptionStatus]);

  // Periodic refresh for trial users and pending payment (every 10 min) and on tab re-focus
  useEffect(() => {
    const tier = subscriptionData?.subscription_tier;
    if (!tier || tier === 'expired') return;
    if (tier !== 'trial' && tier !== 'pending_payment' && tier !== 'active' && tier !== 'grace') return;

    const POLL_MS = tier === 'pending_payment' ? 60 * 1000 : 10 * 60 * 1000;
    const interval = setInterval(fetchSubscriptionStatus, POLL_MS);

    const onFocus = () => fetchSubscriptionStatus();
    window.addEventListener('focus', onFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [subscriptionData?.subscription_tier, fetchSubscriptionStatus]);

  const value = {
    subscriptionData,
    setSubscriptionData,
    previewMode,
    setPreviewMode,
    stripeSlideout,
    setStripeSlideout,
    showSubscribedWelcome,
    setShowSubscribedWelcome,
    previewBannerDismissed,
    setPreviewBannerDismissed,
    previewFlash,
    trialExpired,
    setTrialExpired,
    checkoutReturnState,
    setCheckoutReturnState,
    navigateToBillingPortal,
    openStripeCheckout,
    fetchSubscriptionStatus,
  };

  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
}
